// TourTalk — the AddVendTour community feed. One-page experience with:
//   • Hero + tagline
//   • Inline compose (category picker, body, optional photo + link)
//   • Filter chips per category
//   • Feed of posts with like / comment / share (copy link)
//   • Inline comment thread revealed on click
import { useEffect, useMemo, useState, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import ImageUploader from "@/components/ImageUploader";
import {
  Heart, MessageCircle, Share2, Send, Trash2, Image as ImageIcon,
  X as XIcon, MessageSquareText, ExternalLink, ShieldCheck,
  CalendarClock, Megaphone, Tag, HelpCircle, Handshake, Camera,
  Users, HeartHandshake, Sparkles,
} from "lucide-react";

// Category taxonomy — icon + label + color per type.
export const CATEGORIES = [
  { value: "event",           label: "Event",            icon: CalendarClock,  bg: "rgba(196,79,54,0.10)",  ink: "var(--terracotta)" },
  { value: "business_update", label: "Business update",  icon: Megaphone,      bg: "rgba(75,111,140,0.12)", ink: "#2f4b66" },
  { value: "vendor_special",  label: "Vendor special",   icon: Tag,             bg: "rgba(212,171,79,0.16)", ink: "#7a5b12" },
  { value: "question",        label: "Question",         icon: HelpCircle,     bg: "rgba(120,90,140,0.12)", ink: "#4a2e63" },
  { value: "collaboration",   label: "Collaboration",    icon: Handshake,      bg: "rgba(111,125,85,0.14)", ink: "var(--moss)" },
  { value: "photo",           label: "Photo",            icon: Camera,          bg: "rgba(0,0,0,0.06)",       ink: "var(--ink)" },
  { value: "announcement",    label: "Announcement",     icon: Sparkles,        bg: "rgba(196,79,54,0.10)",  ink: "var(--terracotta)" },
  { value: "volunteer",       label: "Volunteer",        icon: Users,           bg: "rgba(111,125,85,0.14)", ink: "var(--moss)" },
  { value: "giveback",        label: "Give Back",        icon: HeartHandshake, bg: "rgba(196,79,54,0.10)",  ink: "var(--terracotta)" },
];
const CAT = Object.fromEntries(CATEGORIES.map((c) => [c.value, c]));

function timeAgo(iso) {
  if (!iso) return "";
  const diff = (Date.now() - new Date(iso).getTime()) / 1000;
  if (diff < 60) return "just now";
  if (diff < 3600) return `${Math.floor(diff / 60)}m`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h`;
  if (diff < 86400 * 7) return `${Math.floor(diff / 86400)}d`;
  return new Date(iso).toLocaleDateString();
}


export default function TourTalk() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [category, setCategory] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const params = { limit: 60 };
      if (category) params.category = category;
      const { data } = await api.get("/tourtalk/feed", { params });
      setItems(data.items || []);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  }, [category]);

  useEffect(() => { load(); }, [load]);

  const onPosted = (created) => setItems((xs) => [created, ...xs]);
  const onDeleted = (id) => setItems((xs) => xs.filter((p) => p.id !== id));
  const onPatched = (p) => setItems((xs) => xs.map((x) => x.id === p.id ? p : x));

  return (
    <div className="mx-auto max-w-3xl px-6 py-14" data-testid="tourtalk-page">
      <div className="text-center">
        <div
          className="inline-flex items-center gap-2 text-xs uppercase tracking-widest px-3 py-1 rounded-full mb-4"
          style={{ background: "rgba(75,111,140,0.10)", color: "#2f4b66" }}
        >
          <MessageSquareText size={12} /> TourTalk
        </div>
        <h1 className="font-serif text-5xl sm:text-6xl leading-tight">
          Where the AddVendTour community connects.
        </h1>
        <p className="mt-4 text-base max-w-xl mx-auto" style={{ color: "var(--ink-soft)" }}>
          Share what you&rsquo;re building, ask the crowd, drop a special,
          announce your next market. Everything about the tour, in one feed.
        </p>
      </div>

      {user ? (
        <Composer onPosted={onPosted} />
      ) : (
        <div
          className="mt-10 rounded-lg border p-6 text-center"
          style={{ borderColor: "rgba(0,0,0,0.1)", background: "rgba(0,0,0,0.03)" }}
        >
          <p className="text-sm" style={{ color: "var(--ink-soft)" }}>
            <Link to="/login?next=/tourtalk" className="underline" style={{ color: "var(--terracotta)" }}>
              Sign in
            </Link>{" "}
            to post, like, and comment on TourTalk.
          </p>
        </div>
      )}

      <div className="mt-10 flex flex-wrap gap-2" data-testid="tourtalk-filters">
        <FilterChip active={category === ""} onClick={() => setCategory("")} label="All" />
        {CATEGORIES.map((c) => (
          <FilterChip
            key={c.value}
            active={category === c.value}
            onClick={() => setCategory(c.value)}
            label={c.label}
            icon={c.icon}
            testId={`tourtalk-filter-${c.value}`}
          />
        ))}
      </div>

      {error && (
        <div className="mt-6 rounded-md p-3 text-sm"
             style={{ background: "rgba(196,79,54,0.08)", color: "var(--terracotta)" }}>
          {error}
        </div>
      )}

      <div className="mt-8 space-y-4">
        {loading ? (
          <div className="text-sm text-center py-16" style={{ color: "var(--ink-soft)" }}>
            Loading the feed…
          </div>
        ) : items.length === 0 ? (
          <div
            data-testid="tourtalk-empty-state"
            className="py-16 text-center rounded-lg border"
            style={{ borderColor: "rgba(0,0,0,0.1)", background: "rgba(0,0,0,0.02)" }}
          >
            <div className="font-serif text-2xl">Nothing here yet.</div>
            <p className="mt-2 text-sm" style={{ color: "var(--ink-soft)" }}>
              Be the first to start the conversation.
            </p>
          </div>
        ) : (
          items.map((p) => (
            <PostCard
              key={p.id}
              post={p}
              currentUser={user}
              onDeleted={() => onDeleted(p.id)}
              onPatched={onPatched}
            />
          ))
        )}
      </div>
    </div>
  );
}


function FilterChip({ active, onClick, label, icon: Icon, testId }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testId || "tourtalk-filter-all"}
      className="text-xs font-medium px-3 py-1.5 rounded-full inline-flex items-center gap-1.5 transition"
      style={{
        background: active ? "var(--ink)" : "rgba(0,0,0,0.05)",
        color: active ? "#fff" : "var(--ink)",
      }}
    >
      {Icon && <Icon size={12} />} {label}
    </button>
  );
}


function Composer({ onPosted }) {
  const [category, setCategory] = useState("business_update");
  const [body, setBody] = useState("");
  const [photoUrl, setPhotoUrl] = useState("");
  const [linkUrl, setLinkUrl] = useState("");
  const [showLink, setShowLink] = useState(false);
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (body.trim().length < 3) {
      toast.error("Say a little more so the community can chime in.");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post("/tourtalk/posts", {
        category,
        body: body.trim(),
        photo_url: photoUrl || null,
        link_url: linkUrl.trim() || null,
      });
      toast.success("Posted to TourTalk");
      onPosted(data);
      setBody(""); setPhotoUrl(""); setLinkUrl(""); setShowLink(false);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally { setBusy(false); }
  };

  return (
    <form
      onSubmit={submit}
      data-testid="tourtalk-composer"
      className="mt-10 rounded-lg border p-5"
      style={{ borderColor: "rgba(0,0,0,0.12)", background: "#fff" }}
    >
      <div className="text-xs uppercase tracking-widest mb-2" style={{ color: "var(--ink-soft)" }}>
        Share with the community
      </div>
      <div className="flex flex-wrap gap-1.5 mb-3">
        {CATEGORIES.map((c) => {
          const active = category === c.value;
          const Icon = c.icon;
          return (
            <button
              key={c.value}
              type="button"
              onClick={() => setCategory(c.value)}
              data-testid={`tourtalk-composer-cat-${c.value}`}
              className="text-xs px-2.5 py-1 rounded-full inline-flex items-center gap-1 transition"
              style={{
                background: active ? c.ink : c.bg,
                color: active ? "#fff" : c.ink,
              }}
            >
              <Icon size={11} /> {c.label}
            </button>
          );
        })}
      </div>
      <Textarea
        data-testid="tourtalk-composer-body"
        value={body}
        onChange={(e) => setBody(e.target.value)}
        rows={3}
        maxLength={2000}
        placeholder="What's happening in your corner of the tour?"
      />
      {photoUrl && (
        <div className="mt-3 relative inline-block">
          <img
            src={photoUrl}
            alt=""
            className="max-h-48 rounded-md border"
            style={{ borderColor: "rgba(0,0,0,0.12)" }}
            data-testid="tourtalk-composer-photo-preview"
          />
          <button
            type="button"
            onClick={() => setPhotoUrl("")}
            data-testid="tourtalk-composer-photo-clear"
            className="absolute -top-2 -right-2 bg-white rounded-full p-1 shadow"
            style={{ border: "1px solid rgba(0,0,0,0.15)" }}
            aria-label="Remove photo"
          >
            <XIcon size={12} />
          </button>
        </div>
      )}
      {showLink && (
        <Input
          data-testid="tourtalk-composer-link"
          value={linkUrl}
          onChange={(e) => setLinkUrl(e.target.value)}
          placeholder="Optional link (event page, campaign, RSVP…)"
          className="mt-3"
          type="url"
        />
      )}
      <div className="mt-4 flex flex-wrap items-center gap-2">
        <ImageUploader
          onUploaded={setPhotoUrl}
          label={photoUrl ? "Replace photo" : "Add photo"}
          testId="tourtalk-composer-photo"
        />
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={() => setShowLink((v) => !v)}
          className="rounded-full inline-flex items-center gap-1"
          data-testid="tourtalk-composer-toggle-link"
        >
          <ExternalLink size={13} /> {showLink ? "Hide link" : "Add link"}
        </Button>
        <div className="flex-1" />
        <div className="text-xs" style={{ color: "var(--ink-soft)" }}>
          {body.length}/2000
        </div>
        <Button
          type="submit"
          data-testid="tourtalk-composer-submit"
          disabled={busy || body.trim().length < 3}
          className="btn-primary rounded-full inline-flex items-center gap-1.5 px-5"
        >
          <Send size={13} /> {busy ? "Posting…" : "Post"}
        </Button>
      </div>
    </form>
  );
}


function PostCard({ post, currentUser, onDeleted, onPatched }) {
  const cat = CAT[post.category] || CAT.business_update;
  const Icon = cat.icon;
  const [liked, setLiked] = useState(!!post.liked_by_me);
  const [likeCount, setLikeCount] = useState(post.like_count || 0);
  const [showComments, setShowComments] = useState(false);
  const [comments, setComments] = useState(null); // null = not loaded yet
  const [commentBody, setCommentBody] = useState("");
  const [busy, setBusy] = useState(false);

  const toggleLike = async () => {
    if (!currentUser) { window.location.href = "/login?next=/tourtalk"; return; }
    // Optimistic
    const next = !liked;
    setLiked(next);
    setLikeCount((c) => Math.max(0, c + (next ? 1 : -1)));
    try {
      await api.post(`/tourtalk/posts/${post.id}/like`);
    } catch (err) {
      setLiked(!next);
      setLikeCount((c) => Math.max(0, c + (next ? -1 : 1)));
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    }
  };

  const openComments = async () => {
    setShowComments((v) => !v);
    if (comments === null) {
      try {
        const { data } = await api.get(`/tourtalk/posts/${post.id}/comments`);
        setComments(data.items || []);
      } catch (err) {
        toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
      }
    }
  };

  const submitComment = async (e) => {
    e.preventDefault();
    if (commentBody.trim().length < 1) return;
    if (!currentUser) { window.location.href = "/login?next=/tourtalk"; return; }
    setBusy(true);
    try {
      const { data } = await api.post(`/tourtalk/posts/${post.id}/comments`, {
        body: commentBody.trim(),
      });
      setComments((cs) => [...(cs || []), data]);
      setCommentBody("");
      onPatched({ ...post, comment_count: (post.comment_count || 0) + 1 });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally { setBusy(false); }
  };

  const deleteComment = async (cid) => {
    if (!window.confirm("Delete this comment?")) return;
    try {
      await api.delete(`/tourtalk/comments/${cid}`);
      setComments((cs) => (cs || []).filter((c) => c.id !== cid));
      onPatched({ ...post, comment_count: Math.max(0, (post.comment_count || 1) - 1) });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    }
  };

  const deletePost = async () => {
    if (!window.confirm("Delete this post?")) return;
    try {
      await api.delete(`/tourtalk/posts/${post.id}`);
      toast.success("Post deleted");
      onDeleted();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    }
  };

  const share = async () => {
    const url = `${window.location.origin}/tourtalk#post-${post.id}`;
    try {
      if (navigator.share) {
        await navigator.share({ title: "TourTalk", url });
      } else {
        await navigator.clipboard.writeText(url);
        toast.success("Link copied to clipboard");
      }
    } catch { /* user canceled — no toast */ }
  };

  const canDelete = post.is_owner || currentUser?.is_admin;

  return (
    <div
      id={`post-${post.id}`}
      data-testid={`tourtalk-post-${post.id}`}
      className="rounded-lg border p-5"
      style={{ borderColor: "rgba(0,0,0,0.1)", background: "#fff" }}
    >
      <div className="flex items-start gap-3">
        <div
          className="w-10 h-10 rounded-full inline-flex items-center justify-center shrink-0 overflow-hidden"
          style={{ background: cat.bg, color: cat.ink }}
        >
          {post.author.avatar_url
            ? <img src={post.author.avatar_url} alt="" className="w-full h-full object-cover" />
            : <Icon size={18} />}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex flex-wrap items-baseline gap-2">
            {post.author.slug ? (
              <Link to={`/@${post.author.slug}`} className="font-medium hover:underline">
                {post.author.name}
              </Link>
            ) : (
              <span className="font-medium">{post.author.name}</span>
            )}
            {post.author.is_verified && (
              <span
                className="inline-flex items-center gap-0.5 text-[10px] font-semibold uppercase tracking-widest px-1.5 py-0.5 rounded-full"
                style={{ background: "rgba(132,204,22,0.14)", color: "#4B7A0A" }}
              >
                <ShieldCheck size={10} /> Verified
              </span>
            )}
            <span className="text-xs" style={{ color: "var(--ink-soft)" }}>·</span>
            <span
              className="inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-widest px-2 py-0.5 rounded-full"
              style={{ background: cat.bg, color: cat.ink }}
            >
              <Icon size={10} /> {cat.label}
            </span>
            <span className="text-xs" style={{ color: "var(--ink-soft)" }}>·</span>
            <span className="text-xs" style={{ color: "var(--ink-soft)" }}>{timeAgo(post.created_at)}</span>
          </div>
          <div className="mt-2 whitespace-pre-line" data-testid={`tourtalk-post-body-${post.id}`}>{post.body}</div>
          {post.photo_url && (
            <div className="mt-3">
              <img
                src={post.photo_url}
                alt=""
                loading="lazy"
                className="rounded-md border max-h-[420px] w-auto"
                style={{ borderColor: "rgba(0,0,0,0.1)" }}
                data-testid={`tourtalk-post-photo-${post.id}`}
              />
            </div>
          )}
          {post.link_url && (
            <a
              href={post.link_url}
              target="_blank"
              rel="noopener noreferrer"
              className="mt-3 inline-flex items-center gap-1 text-sm underline"
              style={{ color: "var(--terracotta)" }}
              data-testid={`tourtalk-post-link-${post.id}`}
            >
              <ExternalLink size={12} /> {post.link_url}
            </a>
          )}

          <div className="mt-4 pt-3 border-t flex items-center gap-1"
               style={{ borderColor: "rgba(0,0,0,0.08)" }}>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={toggleLike}
              data-testid={`tourtalk-like-${post.id}`}
              className="rounded-full inline-flex items-center gap-1.5"
              style={{ color: liked ? "var(--terracotta)" : "var(--ink-soft)" }}
            >
              <Heart size={14} fill={liked ? "currentColor" : "none"} />
              <span className="text-xs">{likeCount}</span>
            </Button>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={openComments}
              data-testid={`tourtalk-comments-toggle-${post.id}`}
              className="rounded-full inline-flex items-center gap-1.5"
              style={{ color: "var(--ink-soft)" }}
            >
              <MessageCircle size={14} />
              <span className="text-xs">{post.comment_count || 0}</span>
            </Button>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={share}
              data-testid={`tourtalk-share-${post.id}`}
              className="rounded-full inline-flex items-center gap-1.5"
              style={{ color: "var(--ink-soft)" }}
            >
              <Share2 size={14} />
            </Button>
            <div className="flex-1" />
            {canDelete && (
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={deletePost}
                data-testid={`tourtalk-delete-${post.id}`}
                className="rounded-full inline-flex items-center gap-1.5"
                style={{ color: "var(--terracotta)" }}
              >
                <Trash2 size={13} />
              </Button>
            )}
          </div>

          {showComments && (
            <div className="mt-4 pt-3 border-t space-y-3"
                 style={{ borderColor: "rgba(0,0,0,0.08)" }}
                 data-testid={`tourtalk-comments-${post.id}`}>
              {comments === null ? (
                <div className="text-xs" style={{ color: "var(--ink-soft)" }}>Loading…</div>
              ) : comments.length === 0 ? (
                <div className="text-xs" style={{ color: "var(--ink-soft)" }}>
                  Be the first to reply.
                </div>
              ) : (
                comments.map((c) => (
                  <div
                    key={c.id}
                    data-testid={`tourtalk-comment-${c.id}`}
                    className="text-sm flex items-start gap-3"
                  >
                    <div
                      className="w-7 h-7 rounded-full inline-flex items-center justify-center shrink-0 overflow-hidden"
                      style={{ background: "rgba(0,0,0,0.05)" }}
                    >
                      {c.author.avatar_url
                        ? <img src={c.author.avatar_url} alt="" className="w-full h-full object-cover" />
                        : <span className="text-[10px] font-medium">
                            {(c.author.name || "?").slice(0, 1)}
                          </span>}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-xs">
                        <strong>{c.author.name}</strong>
                        <span className="mx-1.5" style={{ color: "var(--ink-soft)" }}>·</span>
                        <span style={{ color: "var(--ink-soft)" }}>{timeAgo(c.created_at)}</span>
                      </div>
                      <div className="whitespace-pre-line">{c.body}</div>
                    </div>
                    {(c.is_owner || currentUser?.is_admin) && (
                      <button
                        type="button"
                        onClick={() => deleteComment(c.id)}
                        className="opacity-50 hover:opacity-100"
                        data-testid={`tourtalk-comment-delete-${c.id}`}
                        aria-label="Delete comment"
                      >
                        <Trash2 size={12} />
                      </button>
                    )}
                  </div>
                ))
              )}
              {currentUser && (
                <form onSubmit={submitComment} className="flex gap-2 pt-2"
                      data-testid={`tourtalk-comment-form-${post.id}`}>
                  <Input
                    value={commentBody}
                    onChange={(e) => setCommentBody(e.target.value)}
                    placeholder="Write a reply…"
                    maxLength={800}
                    data-testid={`tourtalk-comment-input-${post.id}`}
                  />
                  <Button
                    type="submit"
                    size="sm"
                    disabled={busy || commentBody.trim().length < 1}
                    className="btn-primary rounded-full inline-flex items-center gap-1"
                    data-testid={`tourtalk-comment-submit-${post.id}`}
                  >
                    <Send size={12} />
                  </Button>
                </form>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
